import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Composition (The Verse's volley, Act III-5) — the pattern half of the
 * player-authored escalation. The *offering* of cards lives in
 * systems/act3/VerseSystem.js, because it has to happen on a phase transition
 * whether or not this pattern happens to be the one the scheduler picked; what
 * lives here is what the picks actually sound like.
 *
 * The Verse fires a plain expanding fan. Each pick the player handed it bends
 * that fan a different way, and they stack:
 *
 *  - `denser`   more bullets per fan, same speed — a wall problem
 *  - `tracking` the fan homes, slowly — a positioning problem
 *  - `swift`    the fan flies faster — a reaction problem
 *
 * (`narrow`, the shrinking arena, is an arena rule rather than a volley, so
 * VerseSystem owns that one.) Reading `boss.versePicks` rather than holding its
 * own copy is deliberate: the picks belong to the fight, not to whichever
 * instance of this pattern happens to be running when they're made.
 */
export class Composition extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Composition', telegraphDuration: 0.8, activeDuration: 4.4, minPhase });
    this.config = config;
  }

  onBegin() {
    this._fanTimer = 0;
    this._spin = Math.random() * Math.PI * 2;
  }

  onActiveTick(dt, boss, player, pool) {
    this._fanTimer -= dt;
    if (this._fanTimer > 0) return;

    // `forcedPicks` is how a fight can carry a written line without a
    // VerseSystem to write it — the Verse-Fragment herald (ACT3-BIBLE §4) is
    // "one pick, one card, taken automatically", so it simply arrives already
    // holding one and the player fights the result they never chose.
    const picks = boss.versePicks ?? new Set(this.config.forcedPicks ?? []);
    const count = this.config.count + (picks.has('denser') ? this.config.denserBonus : 0);
    const speed = this.config.speed * (picks.has('swift') ? this.config.swiftMultiplier : 1);
    const homing = picks.has('tracking');

    this._fanTimer = this.config.interval;
    // Each fan is offset from the last by an irrational-ish step so successive
    // volleys interleave into a rosette rather than re-treading one set of lanes.
    this._spin += 0.61;
    for (let i = 0; i < count; i++) {
      const angle = this._spin + (i / count) * Math.PI * 2;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 3,
        damage: this.config.damage,
        color: this.config.color,
        lifetime: 3.4,
        homing,
        turnRate: this.config.turnRate,
        target: homing ? player : null,
      });
    }
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH) return null;
    return { kind: 'flash', x: boss.x, y: boss.y, color: this.config.color };
  }
}
